import { endActiveLaborContinuity } from '../../offline/offlineSession';
import { assertClinicalChartReady } from './clinicalMutationAdapter';
import config from '../../config';
import { getAuthHeaders, handleResponse } from './common';
import { dispatchMileagePrompt } from '../mileageEvents';

const chartLockApi = {
  // ========================================
  // Prenatal Visits (flat: no pregnancy ref needed)
  // ========================================
  lockPrenatalVisit: async (visitRefId) => {
    await assertClinicalChartReady({ resourceType: 'prenatalVisit', resourceReferenceId: visitRefId });
    const res = await fetch(`${config.api.url}/pregnancies/prenatal-visits/${visitRefId}/lock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },
  unlockPrenatalVisit: async (visitRefId) => {
    const res = await fetch(`${config.api.url}/pregnancies/prenatal-visits/${visitRefId}/unlock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },
  addPrenatalVisitAddendum: async (visitRefId, text) => {
    const res = await fetch(`${config.api.url}/pregnancies/prenatal-visits/${visitRefId}/addendum`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ text })
    });
    return handleResponse(res);
  },

  // ========================================
  // Postpartum Visits (flat: no pregnancy ref needed)
  // ========================================
  lockPostpartumVisit: async (visitRefId) => {
    await assertClinicalChartReady({ resourceType: 'postpartumVisit', resourceReferenceId: visitRefId });
    const res = await fetch(`${config.api.url}/pregnancies/postpartum-visits/${visitRefId}/lock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },
  unlockPostpartumVisit: async (visitRefId) => {
    const res = await fetch(`${config.api.url}/pregnancies/postpartum-visits/${visitRefId}/unlock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },
  addPostpartumVisitAddendum: async (visitRefId, text) => {
    const res = await fetch(`${config.api.url}/pregnancies/postpartum-visits/${visitRefId}/addendum`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ text })
    });
    return handleResponse(res);
  },

  // ========================================
  // Newborn Visits (flat: no pregnancy ref needed)
  // ========================================
  lockNewbornVisit: async (visitRefId) => {
    await assertClinicalChartReady({ resourceType: 'newbornVisit', resourceReferenceId: visitRefId });
    const res = await fetch(`${config.api.url}/pregnancies/newborn-visits/${visitRefId}/lock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },
  unlockNewbornVisit: async (visitRefId) => {
    const res = await fetch(`${config.api.url}/pregnancies/newborn-visits/${visitRefId}/unlock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },
  addNewbornVisitAddendum: async (visitRefId, text) => {
    const res = await fetch(`${config.api.url}/pregnancies/newborn-visits/${visitRefId}/addendum`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ text })
    });
    return handleResponse(res);
  },

  // ========================================
  // Well-Woman Visits (scoped under /clients/{clientRefId})
  // ========================================
  lockWellWomanVisit: async (clientRefId, visitRefId) => {
    await assertClinicalChartReady({ resourceType: 'wellWomanVisit', resourceReferenceId: visitRefId, parentReferenceId: clientRefId });
    const res = await fetch(`${config.api.url}/clients/${clientRefId}/well-woman-visits/${visitRefId}/lock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },
  unlockWellWomanVisit: async (clientRefId, visitRefId) => {
    const res = await fetch(`${config.api.url}/clients/${clientRefId}/well-woman-visits/${visitRefId}/unlock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },
  addWellWomanVisitAddendum: async (clientRefId, visitRefId, text) => {
    const res = await fetch(`${config.api.url}/clients/${clientRefId}/well-woman-visits/${visitRefId}/addendum`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ text })
    });
    return handleResponse(res);
  },

  // ========================================
  // Call Logs (scoped under /clients/{clientRefId})
  // ========================================
  lockCallLog: async (clientRefId, callLogRefId, fallbackClientId) => {
    const res = await fetch(`${config.api.url}/clients/${clientRefId}/call-logs/${callLogRefId}/lock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!res.ok && fallbackClientId && String(fallbackClientId) !== String(clientRefId)) {
      const fallbackRes = await fetch(`${config.api.url}/clients/${fallbackClientId}/call-logs/${callLogRefId}/lock`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(fallbackRes);
    }
    return handleResponse(res);
  },
  unlockCallLog: async (clientRefId, callLogRefId, fallbackClientId) => {
    const res = await fetch(`${config.api.url}/clients/${clientRefId}/call-logs/${callLogRefId}/unlock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!res.ok && fallbackClientId && String(fallbackClientId) !== String(clientRefId)) {
      const fallbackRes = await fetch(`${config.api.url}/clients/${fallbackClientId}/call-logs/${callLogRefId}/unlock`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      return handleResponse(fallbackRes);
    }
    return handleResponse(res);
  },
  addCallLogAddendum: async (clientRefId, callLogRefId, text, fallbackClientId) => {
    const res = await fetch(`${config.api.url}/clients/${clientRefId}/call-logs/${callLogRefId}/addendum`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ text })
    });
    if (!res.ok && fallbackClientId && String(fallbackClientId) !== String(clientRefId)) {
      const fallbackRes = await fetch(`${config.api.url}/clients/${fallbackClientId}/call-logs/${callLogRefId}/addendum`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ text })
      });
      return handleResponse(fallbackRes);
    }
    return handleResponse(res);
  },

  // ========================================
  // Lactation Visits (scoped under /pregnancies/{pregnancyRefId})
  // ========================================
  lockLactationVisit: async (pregnancyRefId, visitRefId) => {
    await assertClinicalChartReady({ resourceType: 'lactationVisit', resourceReferenceId: visitRefId, parentReferenceId: pregnancyRefId });
    const res = await fetch(`${config.api.url}/pregnancies/${pregnancyRefId}/lactation-visits/${visitRefId}/lock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },
  unlockLactationVisit: async (pregnancyRefId, visitRefId) => {
    const res = await fetch(`${config.api.url}/pregnancies/${pregnancyRefId}/lactation-visits/${visitRefId}/unlock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },
  addLactationVisitAddendum: async (pregnancyRefId, visitRefId, text) => {
    const res = await fetch(`${config.api.url}/pregnancies/${pregnancyRefId}/lactation-visits/${visitRefId}/addendum`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ text })
    });
    return handleResponse(res);
  },

  // ========================================
  // Birth Events (scoped under /babies/{babyRefId})
  // ========================================
  lockBirthEvent: async (babyRefId, birthEventRefId) => {
    await assertClinicalChartReady({ resourceType: 'birthEvent', resourceReferenceId: birthEventRefId, parentReferenceId: babyRefId });
    const res = await fetch(`${config.api.url}/babies/${babyRefId}/birth-events/${birthEventRefId}/lock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    const result = await handleResponse(res);
    dispatchMileagePrompt('BirthEvent', birthEventRefId);
    return result;
  },
  lockBirthRecord: async (birthEventRefId) => {
    await assertClinicalChartReady({ resourceType: 'laborEpisode', resourceReferenceId: birthEventRefId });
    const res = await fetch(`${config.api.url}/birth-events/${birthEventRefId}/lock-birth-record`, {
      method: 'POST', headers: getAuthHeaders()
    });
    const result = await handleResponse(res);
    endActiveLaborContinuity({ episodeReferenceId: birthEventRefId });
    dispatchMileagePrompt('BirthEvent', birthEventRefId);
    return result;
  },
  lockBirthEventDirect: async (birthEventRefId) => {
    await assertClinicalChartReady({ resourceType: 'laborEpisode', resourceReferenceId: birthEventRefId });
    const res = await fetch(`${config.api.url}/birth-events/${birthEventRefId}/lock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    const result = await handleResponse(res);
    endActiveLaborContinuity({ episodeReferenceId: birthEventRefId });
    dispatchMileagePrompt('BirthEvent', birthEventRefId);
    return result;
  },
  unlockBirthEvent: async (babyRefId, birthEventRefId) => {
    const res = await fetch(`${config.api.url}/babies/${babyRefId}/birth-events/${birthEventRefId}/unlock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },
  unlockBirthEventDirect: async (birthEventRefId) => {
    const res = await fetch(`${config.api.url}/birth-events/${birthEventRefId}/unlock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },
  addBirthEventAddendum: async (babyRefId, birthEventRefId, text) => {
    const res = await fetch(`${config.api.url}/babies/${babyRefId}/birth-events/${birthEventRefId}/addendum`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ text })
    });
    return handleResponse(res);
  },
  addBirthEventAddendumDirect: async (birthEventRefId, text) => {
    const res = await fetch(`${config.api.url}/birth-events/${birthEventRefId}/addendum`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ text })
    });
    return handleResponse(res);
  },

  // ========================================
  // Postpartum Assessment (scoped under /babies/{babyRefId}/birth-events/{birthEventRefId})
  // ========================================
  lockPostpartumAssessment: async (babyRefId, birthEventRefId) => {
    await assertClinicalChartReady({ resourceType: 'postpartumAssessment', parentReferenceId: birthEventRefId });
    const res = await fetch(`${config.api.url}/babies/${babyRefId}/birth-events/${birthEventRefId}/postpartum-assessment/lock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },
  unlockPostpartumAssessment: async (babyRefId, birthEventRefId) => {
    const res = await fetch(`${config.api.url}/babies/${babyRefId}/birth-events/${birthEventRefId}/postpartum-assessment/unlock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },
  addPostpartumAssessmentAddendum: async (babyRefId, birthEventRefId, text) => {
    const res = await fetch(`${config.api.url}/babies/${babyRefId}/birth-events/${birthEventRefId}/postpartum-assessment/addendum`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ text })
    });
    return handleResponse(res);
  },

  // ========================================
  // Antepartum Transfers
  // ========================================
  lockAntepartumTransfer: async (transferRefId) => {
    const res = await fetch(`${config.api.url}/transfers/antepartum/${transferRefId}/lock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },
  unlockAntepartumTransfer: async (transferRefId) => {
    const res = await fetch(`${config.api.url}/transfers/antepartum/${transferRefId}/unlock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },
  addAntepartumTransferAddendum: async (transferRefId, text) => {
    const res = await fetch(`${config.api.url}/transfers/antepartum/${transferRefId}/addendum`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ text })
    });
    return handleResponse(res);
  },

  // ========================================
  // Demise Documentation
  // ========================================
  lockDemiseDocumentation: async (demiseRefId) => {
    const res = await fetch(`${config.api.url}/transfers/demise/${demiseRefId}/lock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },
  unlockDemiseDocumentation: async (demiseRefId) => {
    const res = await fetch(`${config.api.url}/transfers/demise/${demiseRefId}/unlock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },
  addDemiseDocumentationAddendum: async (demiseRefId, text) => {
    const res = await fetch(`${config.api.url}/transfers/demise/${demiseRefId}/addendum`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ text })
    });
    return handleResponse(res);
  },

  // ========================================
  // Miscarriage Documentation
  // ========================================
  lockMiscarriageDocumentation: async (miscarriageRefId) => {
    const res = await fetch(`${config.api.url}/transfers/miscarriage/${miscarriageRefId}/lock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },
  unlockMiscarriageDocumentation: async (miscarriageRefId) => {
    const res = await fetch(`${config.api.url}/transfers/miscarriage/${miscarriageRefId}/unlock`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },
  addMiscarriageDocumentationAddendum: async (miscarriageRefId, text) => {
    const res = await fetch(`${config.api.url}/transfers/miscarriage/${miscarriageRefId}/addendum`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ text })
    });
    return handleResponse(res);
  }
};

export default chartLockApi;
